import { useState, useEffect, useCallback } from "react";
import apiClient from "../services/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, TrendingUp, TrendingDown } from "lucide-react";
import { formatCurrency } from "../lib/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ReportExportButton } from "../components/ui/report-export-button";
import { useShowOffice } from "../hooks/useShowOffice";
const ReportsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showOffice } = useShowOffice();

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await apiClient.get(`/reports/profit-loss`);
        // Filter to show only active and completed jobs
        const filteredData = response.data.filter(
          (r) => r.status === "active" || r.status === "complete"
        );
        setReport(filteredData);
      } catch (error) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [getAuthHeaders]);

  // Filter out Office job when showOffice is false
  const filteredReport = showOffice 
    ? report 
    : report.filter(r => r.job_name !== "Office");

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await apiClient.get(`/reports/profit-loss`);
        // Filter to show only active and completed jobs
        const filteredData = response.data.filter(
          (r) => r.status === "active" || r.status === "complete"
        );
        setReport(filteredData);
      } catch (error) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [getAuthHeaders]);

  const getStatusBadge = (status) => {
    const variants = {
      bidding: "badge-bidding",
      precon: "badge-precon",
      active: "badge-active",
      complete: "badge-complete",
      archive: "badge-archive",
      bid_complete: "badge-bid-complete",
      not_bid: "badge-archive",
      not_won: "badge-archive",
    };
    const labels = {
      bidding: "Bidding",
      precon: "Precon",
      active: "Active",
      complete: "Complete",
      archive: "Archive",
      bid_complete: "Bid Complete",
      not_bid: "Not Bid",
      not_won: "Not Won",
    };
    return (
      <span className={`badge-status ${variants[status] || "badge-precon"}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Summary calculations
  const totalEstimate = filteredReport.reduce((sum, r) => sum + r.total_estimate, 0);
  const totalActual = filteredReport.reduce((sum, r) => sum + r.total_actual, 0);
  const totalProfitLoss = filteredReport.reduce((sum, r) => sum + r.profit_loss, 0);
  const profitableJobs = filteredReport.filter((r) => r.profit_loss >= 0).length;
  const lossJobs = filteredReport.filter((r) => r.profit_loss < 0).length;

  // Chart data
  const chartData = filteredReport.slice(0, 10).map((r) => ({
    name: r.job_name.length > 15 ? r.job_name.substring(0, 15) + "..." : r.job_name,
    profit_loss: r.profit_loss,
  }));

  const getExcelData = useCallback(() => {
    const headers = [
      "Job Name", "Client", "Status", "Labor Estimate", "Material Estimate",
      "Total Estimate", "Labor Actual", "Material Actual", "Total Actual", "Profit/Loss",
    ];
    const rows = filteredReport.map((r) => [
      r.job_name, r.client_name, r.status, r.labor_estimate, r.material_estimate,
      r.total_estimate, r.labor_actual, r.material_actual, r.total_actual, r.profit_loss,
    ]);
    const csvContent = [headers, ...rows].map((row) => row.join(",")).join("\n");
    return { filename: "profit-loss-report", csvContent };
  }, [filteredReport]);

  const getPdfContent = useCallback(() => {
    const customStyles = `
      .summary { margin-top: 20px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
      .summary-card { border: 1px solid #ddd; padding: 15px; text-align: center; }
      .summary-label { font-size: 12px; color: #666; }
      .summary-value { font-size: 20px; font-weight: bold; }
    `;
    const htmlContent = `
      <h1>Profit/Loss Report</h1>
      <p class="subtitle">Generated ${new Date().toLocaleDateString()}</p>
      <div class="summary">
        <div class="summary-card">
          <div class="summary-label">Total Estimates</div>
          <div class="summary-value">${formatCurrency(totalEstimate)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Total Actuals</div>
          <div class="summary-value">${formatCurrency(totalActual)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Net Profit/Loss</div>
          <div class="summary-value ${totalProfitLoss >= 0 ? 'green' : 'red'}">${formatCurrency(totalProfitLoss)}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Jobs</div>
          <div class="summary-value">${profitableJobs} profit / ${lossJobs} loss</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Job</th><th>Client</th><th>Status</th>
            <th class="text-right">Labor Est</th><th class="text-right">Labor Act</th>
            <th class="text-right">Mat Est</th><th class="text-right">Mat Act</th>
            <th class="text-right">Total Est</th><th class="text-right">Total Act</th>
            <th class="text-right">P/L</th>
          </tr>
        </thead>
        <tbody>
          ${filteredReport.map(r => `
            <tr>
              <td>${r.job_name}</td><td>${r.client_name}</td><td>${r.status}</td>
              <td class="text-right">${formatCurrency(r.labor_estimate)}</td>
              <td class="text-right">${formatCurrency(r.labor_actual)}</td>
              <td class="text-right">${formatCurrency(r.material_estimate)}</td>
              <td class="text-right">${formatCurrency(r.material_actual)}</td>
              <td class="text-right">${formatCurrency(r.total_estimate)}</td>
              <td class="text-right">${formatCurrency(r.total_actual)}</td>
              <td class="text-right ${r.profit_loss >= 0 ? 'green' : 'red'}">${formatCurrency(r.profit_loss)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      <p class="footer">V2R Masonry - Profit/Loss Report</p>
    `;
    return { title: "Profit/Loss Report", htmlContent, customStyles };
  }, [filteredReport, totalEstimate, totalActual, totalProfitLoss, profitableJobs, lossJobs]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">
          Loading report...
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter space-y-8" data-testid="reports-page">
      {/* Header */}
      <div className="page-header flex flex-row items-center justify-between gap-3">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground whitespace-nowrap">
          Profit/Loss Report
        </h1>
        <div className="flex items-center shrink-0 page-header-actions">
          <ReportExportButton
            title="Export Profit/Loss Report"
            getExcelData={getExcelData}
            getPdfContent={getPdfContent}
            compact
            data-testid="download-report-btn"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="stat-card" data-testid="total-estimate-summary">
          <p className="stat-label">Total Estimates</p>
          <p className="stat-value mt-2">{formatCurrency(totalEstimate)}</p>
        </Card>
        <Card className="stat-card" data-testid="total-actual-summary">
          <p className="stat-label">Total Actuals</p>
          <p className="stat-value mt-2">{formatCurrency(totalActual)}</p>
        </Card>
        <Card className="stat-card" data-testid="net-profit-loss-summary">
          <p className="stat-label">Net Profit/Loss</p>
          <p
            className={`stat-value mt-2 ${
              totalProfitLoss >= 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {formatCurrency(totalProfitLoss)}
          </p>
        </Card>
        <Card className="stat-card" data-testid="job-breakdown-summary">
          <p className="stat-label">Job Breakdown</p>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-green-600 font-bold">{profitableJobs}</span>
            </div>
            <div className="flex items-center gap-1">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-red-600 font-bold">{lossJobs}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Chart */}
      {filteredReport.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Profit/Loss by Job</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    tick={{ fontSize: 12 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis
                    tickFormatter={(value) => formatCurrency(value)}
                    tick={{ fontSize: 12 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), "Profit/Loss"]}
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "4px",
                    }}
                  />
                  <Bar dataKey="profit_loss" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.profit_loss >= 0 ? "#22c55e" : "#ef4444"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detailed Table */}
      {filteredReport.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No data yet
            </h3>
            <p className="text-muted-foreground">
              Create jobs and track costs to see your profit/loss report
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Detailed Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="min-w-[200px]">Job</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Labor Est.</TableHead>
                    <TableHead className="text-right">Material Est.</TableHead>
                    <TableHead className="text-right">Labor Act.</TableHead>
                    <TableHead className="text-right">Material Act.</TableHead>
                    <TableHead className="text-right">Profit/Loss</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReport.map((row) => (
                    <TableRow
                      key={row.job_id}
                      className="table-row"
                      data-testid={`report-row-${row.job_id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-bold text-foreground text-base">{row.job_name}</p>
                          <p className="text-sm text-muted-foreground">{row.client_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(row.status)}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.labor_estimate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.material_estimate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.labor_actual)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.material_actual)}
                      </TableCell>
                      <TableCell
                        className={`text-right font-bold ${
                          row.profit_loss >= 0
                            ? "profit-positive"
                            : "profit-negative"
                        }`}
                      >
                        {formatCurrency(row.profit_loss)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell colSpan={2}>Totals</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        filteredReport.reduce((s, r) => s + r.labor_estimate, 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        filteredReport.reduce((s, r) => s + r.material_estimate, 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        filteredReport.reduce((s, r) => s + r.labor_actual, 0)
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(
                        filteredReport.reduce((s, r) => s + r.material_actual, 0)
                      )}
                    </TableCell>
                    <TableCell
                      className={`text-right font-bold ${
                        totalProfitLoss >= 0
                          ? "profit-positive"
                          : "profit-negative"
                      }`}
                    >
                      {formatCurrency(totalProfitLoss)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};

export default ReportsPage;
